import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { uiMarker } from '../recipes/shared';

/**
 * A filtered, keyboard-navigable list — the global search palette, and the
 * "search invoices" picker inside the impound form.
 *
 * cmdk owns the behaviour: arrow keys move the highlight, Enter selects, the
 * list filters as you type, and the whole thing is announced as a combobox.
 *
 *   <Command>
 *     <CommandInput placeholder="Search invoices…" />
 *     <CommandList>
 *       <CommandEmpty>No invoices found.</CommandEmpty>
 *       <CommandGroup heading="Recent">
 *         <CommandItem onSelect={…}>#1042</CommandItem>
 *       </CommandGroup>
 *     </CommandList>
 *   </Command>
 *
 * Pass `shouldFilter={false}` when the results come from a server — otherwise
 * cmdk filters the already-filtered list again and hides matches.
 */
export const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(function Command({ className, ...props }, ref) {
  return (
    <CommandPrimitive
      ref={ref}
      className={cn(
        uiMarker,
        'flex size-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
        className
      )}
      {...props}
    />
  );
});

export const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(function CommandInput({ className, ...props }, ref) {
  return (
    <div className="relay-ui-internal flex items-center gap-2 border-b border-border px-3">
      <Search className="size-icon-sm shrink-0 text-muted-foreground" />
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          'flex h-11 w-full bg-transparent py-3 text-body-sm outline-none',
          'placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      />
    </div>
  );
});

/**
 * Let the mouse wheel scroll the list even under a modal's scroll lock.
 *
 * A Combobox portals its list to the body, so inside a Dialog it sits outside
 * the subtree react-remove-scroll allows and every wheel event over it is
 * cancelled — the list moves by keyboard and by dragging its bar, but not with
 * a wheel. The lock listens on the document, so claiming the event here, before
 * it bubbles that far, is enough; the browser then scrolls the list normally.
 *
 * Only claimed when the list actually has somewhere to go, so a short one still
 * lets the wheel reach whatever is behind it.
 */
function useWheelUnderScrollLock(
  ref: React.RefObject<HTMLElement | null>
): void {
  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const onWheel = (event: WheelEvent) => {
      if (node.scrollHeight > node.clientHeight) event.stopPropagation();
    };

    node.addEventListener('wheel', onWheel);
    return () => node.removeEventListener('wheel', onWheel);
  }, [ref]);
}

export const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(function CommandList({ className, ...props }, ref) {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  useWheelUnderScrollLock(listRef);

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  return (
    <CommandPrimitive.List
      ref={setRefs}
      className={cn(
        'max-h-[300px] overflow-y-auto overflow-x-hidden',
        className
      )}
      {...props}
    />
  );
});

export const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(function CommandEmpty({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Empty
      ref={ref}
      className={cn(
        'py-6 text-center text-body-sm text-muted-foreground',
        className
      )}
      {...props}
    />
  );
});

export const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(function CommandGroup({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Group
      ref={ref}
      className={cn(
        'overflow-hidden p-1 text-foreground',
        // cmdk renders the heading itself, so it can only be styled from here.
        '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5',
        '[&_[cmdk-group-heading]]:text-overline [&_[cmdk-group-heading]]:uppercase',
        '[&_[cmdk-group-heading]]:text-muted-foreground',
        className
      )}
      {...props}
    />
  );
});

export const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(function CommandItem({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-2 text-body-sm outline-none',
        // cmdk drives the highlight from data-selected, not :hover, so the
        // keyboard and the pointer land on the same styling.
        'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
        className
      )}
      {...props}
    />
  );
});

